import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Smartphone, CheckCircle2, LogIn } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/assets";

const APP_SCHEME = "tpnativeapp://auth";

/* Web -> native app session handoff. The mobile app opens this page in a browser;
   after web sign-in we mint a Firebase custom token and deep-link back into the app. */
export default function AppLogin() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | minting | ready | error
  const [deepLink, setDeepLink] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Open in the TravelerPulse App"; }, []);

  useEffect(() => {
    if (loading || !user || status !== "idle") return;
    setStatus("minting");
    api.post("/auth/app-token")
      .then((r) => {
        const url = `${APP_SCHEME}?token=${encodeURIComponent(r.data.token)}`;
        setDeepLink(url);
        setStatus("ready");
        // auto-launch only on mobile — desktop browsers show an ugly "no app" popup
        if (/android|iphone|ipad|ipod/i.test(navigator.userAgent)) window.location.href = url;
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Could not prepare the app sign-in. Please try again.");
        setStatus("error");
      });
  }, [loading, user, status]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-zinc-50 px-5" data-testid="app-login-page">
      <div className="bg-white border border-zinc-300 p-8 max-w-md w-full text-center">
        <img src={BRAND.masthead} alt="TravelerPulse.pro" className="h-12 w-auto mx-auto mb-5 object-contain" />
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
        ) : !user ? (
          <>
            <Smartphone className="w-10 h-10 mx-auto text-[#1a73e8] mb-3" />
            <h1 className="font-heading font-900 text-xl mb-2">Sign in to continue to the app</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Sign in on the website first — we'll send your session straight back to the TravelerPulse app.
            </p>
            <Link to="/login?next=/app-login" data-testid="app-login-signin"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a73e8] text-white text-sm font-800 hover:opacity-90">
              <LogIn className="w-4 h-4" /> Sign in
            </Link>
          </>
        ) : status === "error" ? (
          <>
            <h1 className="font-heading font-900 text-xl mb-2 text-red-600">Something went wrong</h1>
            <p className="text-sm text-muted-foreground" data-testid="app-login-error">{error}</p>
          </>
        ) : status === "ready" ? (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#34A853] mb-3" />
            <h1 className="font-heading font-900 text-xl mb-2">You're signed in!</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Opening the TravelerPulse app… if nothing happens, tap the button below.
            </p>
            <a href={deepLink} data-testid="app-login-open-app"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a73e8] text-white text-sm font-800 hover:opacity-90">
              <Smartphone className="w-4 h-4" /> Open the app
            </a>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1a73e8] mb-3" />
            <p className="text-sm text-muted-foreground">Preparing your app sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
